import * as React from "react";

import { cn } from "../utils";
import { CrumbTrail } from "./crumb-trail";
import { FavoriteToggle } from "../favorite-toggle";
import { HideWhenShellTitled, ShellActionsPortal } from "../shell-title-context";
import { ScopedSearchButton } from "./scoped-search-button";
import { Masthead } from "./masthead";
import { type MetaFact } from "./meta-facts";
import { type NavTab } from "../nav-tabs";

export interface PageHeaderCrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  /** Page title. Renders in the unified serif page-title treatment
   * (`.type-page-title`, 28px Fraunces) — the SAME title every mode section
   * uses, so a standalone page reads identically to a mode page (Matt, 2026-07-16;
   * default-on rollout). Optional: omit it on pages whose name is already shown by
   * an enclosing shell/PageNav to avoid a duplicate heading — pass only
   * `subtitle`/`toolbar` to keep the scope context without the redundant H1. */
  title?: React.ReactNode;
  /** Semantic sibling rendered beside, but outside, the title heading. */
  titleAccessory?: React.ReactNode;
  /** Optional one-line subtitle below the title.
   * @deprecated Prefer `description` — the canonical name for this slot. Still
   * honored; `description` wins when both are set. */
  subtitle?: React.ReactNode;
  /** The one-line description below the title (what the page is FOR, in the
   * house voice — short, plain, not press-release). The canonical name for the
   * former `subtitle` slot; every page carries one
   * (docs/design/PAGE-HEADER-UNIFICATION-PRD.md). */
  description?: React.ReactNode;
  /** The "← Parent" back eyebrow above the title — a single up-link. For a
   * multi-level path use `breadcrumbs` instead; the two are not stacked. */
  back?: { label: string; href?: string; onClick?: () => void };
  /** Icon meta-facts rendered under the description (Domain · Gate: Ship → Verify
   * · Runs as · Live…). Universal + opt-in — most pages pass none; a gated or
   * object-detail surface fills it. */
  meta?: MetaFact[];
  /** Optional in-page sub-nav, rendered as the canonical `NavTabs` pill strip
   * below the masthead. For a page's OWN sub-views (a workflow's
   * Overview/Pipeline/Runs) — NOT the mode view nav, which lives in the ModeRail.
   * Pass query-bearing hrefs (`?tab=…`) for URL nav, or drive it controlled via
   * `navValue`/`onNavSelect`. */
  tabs?: NavTab[];
  /** Controlled-mode selection for `tabs` (local state). Pass with `onNavSelect`
   * to render buttons instead of links. Omit for URL-driven tabs. */
  navValue?: string;
  onNavSelect?: (key: string) => void;
  /** Slot to the right of `tabs` (a report-range select, a kbd hint…). Rendered
   * only when `tabs` is set. */
  navRight?: React.ReactNode;
  /** Optional tiny eyebrow above the title. */
  eyebrow?: React.ReactNode;
  /** Optional breadcrumb trail rendered above the title row. */
  breadcrumbs?: PageHeaderCrumb[];
  /** Right-aligned slot for filters / primary actions. */
  toolbar?: React.ReactNode;
  /**
   * Scoped-search affordance (UI-UX-PATTERNS-PRD Issue 19). When set, a
   * magnifying-glass <ScopedSearchButton> is placed at the START of the toolbar
   * row (before any `toolbar` content) — it focuses the root dock search
   * pre-scoped to the current page; the page mirrors the dock's `?q=` and filters
   * its own rows. Pass a string to override the accessible label (e.g. "Search
   * views"); `true` uses the default "Search". Additive + optional — existing
   * `toolbar` consumers are unaffected. The page still needs a dock scope leg +
   * `scopeForPath` clause for the search to return in-scope results.
   */
  search?: boolean | string;
  /**
   * Show the favorite-toggle star next to the toolbar. Default false because
   * the shell's TopNav already renders a global FavoriteToggle for every
   * page; setting this true gives the same control a second home next to the
   * page title for pages that want it close to the page H1.
   */
  showFavorite?: boolean;
  /**
   * Fallback label for the favorite when document.title is empty. Defaults to
   * the page title (when it's a string).
   */
  favoriteLabel?: string;
  /**
   * Extra classes for the title h1, appended after the serif default
   * (`type-page-title`). An escape hatch for a genuine one-off (e.g. a tone
   * color on a demo). Do NOT re-declare a font-size here — the serif treatment is
   * the standard; the old `type-page-title` step-up is retired.
   */
  titleClassName?: string;
  /**
   * A DETAIL page whose identity IS the page (a workflow, a run — an OBJECT,
   * not a mode leaf): render the back/title band and keep the toolbar inline
   * even under a ModeShell, instead of deferring the title and hoisting the
   * toolbar into the shell's title row. The shell must suppress its own leaf
   * title bar for the route (e.g. AgentsModePulse's detail-route exemption) or
   * the page shows two titles — the two switches are one decision, made in two
   * places. → docs/design/WORKFLOW-DETAIL-PARITY-AUDIT.md RC1.
   */
  ownTitle?: boolean;
  className?: string;
}

/**
 * Single page-chrome block. Use at the top of every top-level product page.
 * Not Card-wrapped — sits flush with the page substrate. The title flow itself
 * is the shared `<Masthead>` (docs/design/PAGE-HEADER-UNIFICATION-PRD.md); this
 * wraps it with the standalone-page concerns: a breadcrumb trail above, the
 * `HideWhenShellTitled` title-dedupe under a mode shell, the scoped-search +
 * favorite affordances, and the `ShellActionsPortal` that lifts a mounted page's
 * actions up to the shell title row.
 */
export function PageHeader({
  title,
  titleAccessory,
  subtitle,
  description,
  back,
  meta,
  tabs,
  navValue,
  onNavSelect,
  navRight,
  eyebrow,
  breadcrumbs,
  toolbar,
  search,
  showFavorite = false,
  favoriteLabel,
  titleClassName,
  ownTitle = false,
  className,
}: PageHeaderProps) {
  // `description` is the canonical name for the one-liner; `subtitle` is the
  // kept-alive alias. When both are set, description wins.
  const desc = description ?? subtitle;
  const hasCrumbs = breadcrumbs && breadcrumbs.length > 0;
  const derivedFavLabel =
    favoriteLabel ?? (typeof title === "string" ? title : undefined);
  const searchButton = search ? (
    <ScopedSearchButton label={typeof search === "string" ? search : undefined} />
  ) : null;
  // Under a ModeShell the shell owns the serif title, so lift this page's actions
  // UP to sit inline with it (the Work▸Issues hoist, generalized); standalone they
  // render inline. See shell-title-context.tsx. An `ownTitle` detail page keeps
  // them inline beside its own title — `shrink-0`, no wrap, matching the
  // prototype's actions cluster so a long toolbar never folds under the title.
  const toolbarNode =
    searchButton || toolbar || showFavorite ? (
      <div
        data-slot="page-header-toolbar"
        className={
          ownTitle
            ? "flex shrink-0 items-center gap-2"
            : "flex flex-wrap items-center gap-2"
        }
      >
        {searchButton}
        {toolbar}
        {showFavorite && <FavoriteToggle defaultLabel={derivedFavLabel} />}
      </div>
    ) : undefined;
  const actions = toolbarNode ? (
    ownTitle ? toolbarNode : <ShellActionsPortal>{toolbarNode}</ShellActionsPortal>
  ) : undefined;

  return (
    <div
      data-slot="page-header"
      className={cn("flex flex-col gap-3", className)}
    >
      {hasCrumbs && (
        // Every crumb with an href is a real link to that level; once the trail
        // runs deeper than three the middle folds into a hover/click `…`
        // (CrumbTrail). The current/leaf crumb renders as plain text.
        <CrumbTrail
          crumbs={breadcrumbs!}
          separator="chevron"
          collapseAfter={3}
          className="flex items-center"
          listClassName="gap-1 type-subtext text-muted-foreground"
          currentClassName="text-foreground"
        />
      )}
      <Masthead
        back={back}
        eyebrow={eyebrow}
        title={title}
        titleAccessory={titleAccessory}
        titleClassName={titleClassName}
        description={desc}
        meta={meta}
        actions={actions}
        tabs={tabs}
        navValue={navValue}
        onNavSelect={onNavSelect}
        navRight={navRight}
        // Back/eyebrow/title defer to an enclosing shell that already renders the
        // page title (ModeShell); the description/meta/actions stay as scope
        // context. Standalone (no shell boundary) they render exactly as before.
        // An `ownTitle` detail page opts out — its identity band always renders.
        identityWrap={
          ownTitle
            ? undefined
            : (node) => <HideWhenShellTitled>{node}</HideWhenShellTitled>
        }
      />
    </div>
  );
}
